import React, { ReactElement } from 'react';
import * as Sentry from '@sentry/react';

import { commitHash } from './version';

export let initializeSentry: () => void = () => {};
export let SentryErrorBoundary = (props: { readonly children: ReactElement }) => props.children;
export let setSentryUser: (user: { email: string; organization: string }) => void = () => {};
export let captureHandledException: (error: Error) => void = () => {};
export let addSentryBreadcrumb: (breadcrumb: Sentry.Breadcrumb) => void = () => {};
export let sentryMetricsCount: (name: string, attributes: Record<string, unknown> | undefined) => void = () => {};
export let sentryMetricsDistribution: (
  name: string,
  valueInMilliseconds: number,
  attributes: Record<string, unknown> | undefined,
) => void = () => {};

if (process.env.PRODUCTION) {
  // This needs to be separate from the above if, so the bundler can potentially strip the whole block in a
  //   non-production environment.
  if (process.env.SENTRY_URL) {
    initializeSentry = () => {
      if (commitHash == null) return;

      Sentry.init({
        release: `${process.env.PROJECT_NAME}@${commitHash}`,
        environment: process.env.APP_ENV,
        dsn: process.env.SENTRY_URL,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            // Additional SDK configuration goes in here, for example:
            maskAllText: false,
            maskAllInputs: false,
            blockAllMedia: false,
          }),
          Sentry.replayCanvasIntegration(),
        ],
        tracesSampleRate: 1.0,
        attachStacktrace: true,

        // Setting this to 1 so we don't miss any sessions.
        replaysSessionSampleRate: 1.0,

        // If the entire session is not sampled, use the below sample rate to sample
        // sessions when an error occurs.
        replaysOnErrorSampleRate: 1.0,
      });
    };

    SentryErrorBoundary = (props) => <Sentry.ErrorBoundary showDialog>{props.children}</Sentry.ErrorBoundary>;

    setSentryUser = (user) => {
      Sentry.setUser({ email: user.email });
      // The organization doesn't show up in the Replay tags if we set it using `setUser`, so we set it as a separate tag instead.
      Sentry.setTag('user.organization', user.organization);
    };

    captureHandledException = (error) => {
      Sentry.showReportDialog({ eventId: Sentry.captureException(error) });
    };

    addSentryBreadcrumb = (breadcrumb) => {
      Sentry.addBreadcrumb(breadcrumb);
    };

    sentryMetricsCount = (name, attributes) => {
      Sentry.metrics.count(name, 1, { attributes });
    };

    sentryMetricsDistribution = (name, valueInMilliseconds, attributes) => {
      Sentry.metrics.distribution(name, valueInMilliseconds, { unit: 'millisecond', attributes });
    };
  }
}
